import type { ComponentProps } from 'react';
import React, { useCallback, useEffect, useId } from 'react';
import type { StyleProp, ViewInstance, ViewProps, ViewStyle } from 'react-native';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

import { useIsFocused, useReduceMotionStatus, useResponsive } from '../../hooks';
import { FocusScope } from '../FocusScope';
import { useDialogFocus } from './hooks';
import { ModalSizesStyles } from './Modal.sizes';

import type { ModalSize } from './Modal.sizes';
import type { BaseModalProps } from './Modal.types';

// Transparent variation of modals where it uses react-navigation instead of react-native.
export const TransparentModal = ({
  testID,
  accessibilityLabel,
  size = 'small',
  dialogMaxWidth,
  dialogMaxHeight,
  fitHeight = false,
  offset,
  children,
  transparent,
  disableAnimation,
  dialogStyles,
  dialogLayout,
  style,
  onClose,
  mobileEdges = [],
  withBackdrop,
  backdropAccessibilityLabel,
}: ModalProps) => {
  const { currentBreakpoint } = useResponsive();
  const isFocused = useIsFocused();
  const dialogId = useId();

  // NOTE: On compact screens the dialog is a full screen whose controls can sit
  //       in the header or next to it, so the screens behind it are made inert
  //       instead of trapping focus inside it.
  const isFullScreen = currentBreakpoint.isXS;

  useDialogFocus({ dialogId, isEnabled: isFocused, isFullScreen, onClose });

  if (isFullScreen) {
    return (
      <SafeAreaView
        testID={testID}
        nativeID={dialogId}
        accessibilityLabel={accessibilityLabel}
        role="dialog"
        tabIndex={-1}
        style={[styles.nativeDialog, style]}
        edges={mobileEdges}
      >
        {children}
      </SafeAreaView>
    );
  }

  const wrapperSmStyles = [styles.wrapperSM, StyleSheet.absoluteFill, style];

  return (
    <View testID={testID} accessibilityViewIsModal style={wrapperSmStyles}>
      <Backdrop
        accessibilityLabel={backdropAccessibilityLabel ?? 'dismiss dialog'}
        onClose={onClose}
        transparent={transparent && !withBackdrop}
      />

      <FocusScope contain={isFocused}>
        <AnimatedModal
          nativeID={dialogId}
          accessibilityLabel={accessibilityLabel}
          size={size}
          fitHeight={fitHeight}
          offset={offset}
          disableAnimation={disableAnimation}
          dialogMaxWidth={dialogMaxWidth}
          dialogMaxHeight={dialogMaxHeight}
          dialogStyles={dialogStyles}
          dialogLayout={dialogLayout}
          style={transparent ? styles.transparentDialog : undefined}
        >
          {children}
        </AnimatedModal>
      </FocusScope>
    </View>
  );
};

const AnimatedModal = ({
  nativeID,
  accessibilityLabel,
  size = 'small',
  fitHeight,
  dialogMaxWidth,
  dialogMaxHeight,
  offset,
  style,
  dialogStyles: dialogExternalStyles,
  dialogLayout,
  children,
  disableAnimation,
}: AnimatedModalProps) => {
  const { minWidth } = useResponsive();
  const reduceMotionStatus = useReduceMotionStatus();
  const noAnimation = reduceMotionStatus === 'enabled' || disableAnimation;
  const distance = size === 'drawer' ? DRAWER_ANIM_DISTANCE : ANIM_DISTANCE;
  const position = useSharedValue(disableAnimation ? 0 : distance);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  // NOTE: The slide starts before the reduce motion setting resolves, so a
  //       late "enabled" status snaps the dialog into place mid-animation.
  const settleDialogPosition = useCallback(() => {
    if (noAnimation) {
      position.set(0);

      return;
    }

    position.set(
      withTiming(0, {
        duration: ANIM_DURATION,
        easing: Easing.out(Easing.exp),
      }),
    );
  }, [noAnimation, position]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(settleDialogPosition, [settleDialogPosition]);

  const initialPosition = size === 'drawer' ? styles.animatedDrawer : styles.animatedDialog;

  // ─── Wrapper Styles ──────────────────────────────────────────────────

  const offsetStyle = useStyle(() => {
    if (!offset) return;

    return {
      width: `${100 - Number(/\d+/.exec(offset)?.[0])}%`,
      height: `${100 - Number(/\d+/.exec(offset)?.[0])}%`,
      maxHeight: `${100 - Number(/\d+/.exec(offset)?.[0])}%`,
    };
  }, [offset]);

  const dialogDynamicStyles = useStyle(() => {
    if (!fitHeight && !dialogMaxHeight && !dialogMaxWidth) return;

    return {
      maxWidth: '100%',
      maxHeight: '100%',

      ...(dialogMaxWidth ? { width: '100%', maxWidth: dialogMaxWidth } : {}),
      ...(dialogMaxHeight ? { height: '100%', maxHeight: dialogMaxHeight } : {}), // prettier-ignore
    };
  }, [dialogMaxWidth, dialogMaxHeight]);

  const sizesWithWrapperSpacing: readonly ModalSize[] = ['small', 'medium', 'large'];
  const shouldAddSpacing = minWidth.isSM && sizesWithWrapperSpacing.includes(size);
  const dialogWrapperSpacingStyles = shouldAddSpacing ? styles.dialogWrapperWithSpacing : {};
  const dialogWrapperStyles = [styles.dialogWrapper, dialogWrapperSpacingStyles];

  // ─── Modal Styles ────────────────────────────────────────────────────

  const dialogAnimatedStyle = useAnimatedStyle(() => {
    const translateModal = { translateY: position.value };
    const translateDrawer = { translateX: position.value };
    const translate = size === 'drawer' ? translateDrawer : translateModal;

    return { transform: [translate] };
  });
  const radiusStyle = ['small', 'medium', 'large'].includes(size) ? styles.dialogRadius : undefined;
  const dialogStyles = [
    styles.dialog,
    radiusStyle,
    ModalSizesStyles[size],
    !noAnimation && initialPosition,
    dialogAnimatedStyle,
    dialogDynamicStyles,
    offsetStyle,
    dialogExternalStyles,
  ];

  // ─────────────────────────────────────────────────────────────────

  return (
    <View style={dialogWrapperStyles} pointerEvents="box-none">
      <Animated.View
        nativeID={nativeID}
        aria-label={accessibilityLabel}
        aria-modal
        role="dialog"
        tabIndex={-1}
        style={dialogStyles}
        layout={dialogLayout}
      >
        <ScrollView
          contentContainerStyle={styles.contentContainer}
          style={[styles.dialogColor, style]}
          scrollEnabled={false}
        >
          {children}
        </ScrollView>
      </Animated.View>
    </View>
  );
};

const Backdrop = React.forwardRef<ViewInstance, BackdropProps>(
  ({ accessibilityLabel, onClose, transparent }, ref) => (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      ref={ref}
      testID="sg-react-navigation-modal-backdrop"
      style={[
        StyleSheet.absoluteFill,
        styles.backdrop,
        transparent ? styles.transparentDialog : undefined,
      ]}
      onPress={onClose}
    />
  ),
);

// ────────────────────────────────────────────────────────────────────────────────

const ANIM_DURATION = 1000; // 1sec
const ANIM_DISTANCE = 15; // 15px
const DRAWER_ANIM_DISTANCE = 480; // 480px

const styles = StyleSheet.create({
  wrapperSM: {
    justifyContent: 'center',
  },

  // ──────── Dialog ─────────
  dialog: {
    overflow: 'hidden',
    maxWidth: '100%',
    margin: 'auto',
    ...theme.elevations['2'],
  },
  dialogRadius: {
    borderRadius: theme.radius.large,
  },
  dialogWrapper: {
    flexGrow: 1,
    maxHeight: '100%',
  },
  dialogWrapperWithSpacing: {
    padding: theme.spacing['10'],
  },
  nativeDialog: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  animatedDialog: {
    transform: [{ translateY: ANIM_DISTANCE }],
  },
  animatedDrawer: {
    transform: [{ translateX: DRAWER_ANIM_DISTANCE }],
  },
  contentContainer: {
    maxHeight: '100%',
    flex: 1,
  },
  dialogColor: {
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  transparentDialog: {
    backgroundColor: theme.colors.OPACITY.TRANSPARENT,
  },

  // ──────── Backdrop ─────────
  backdrop: {
    width: '100%',
    backgroundColor: theme.colors.OPACITY.DARK_KALE.DARKER,
  },
});

// ────────────────────────────────────────────────────────────────────────────────

type BackdropProps = {
  accessibilityLabel: string;
  onClose?: () => void;
  transparent?: boolean;
};

type ModalProps = ActionProps &
  ViewProps &
  SizeProps & {
    withBackdrop?: boolean;
    backdropAccessibilityLabel?: string;
    mobileEdges?: ComponentProps<typeof SafeAreaView>['edges'];
    dialogMaxWidth?: number;
    dialogMaxHeight?: number;
    dialogStyles?: StyleProp<ViewStyle>;
    dialogLayout?: AnimatedViewProps['layout'];
  };

type AnimatedViewProps = ComponentProps<typeof Animated.View>;

type SizeProps = BaseModalProps & {
  transparent?: boolean;
  disableAnimation?: boolean;
};

type AnimatedModalProps = {
  dialogMaxWidth?: number;
  dialogMaxHeight?: number;
  dialogStyles?: StyleProp<ViewStyle>;
  dialogLayout?: AnimatedViewProps['layout'];
} & ViewProps &
  Pick<ModalProps, 'disableAnimation' | 'size' | 'fitHeight' | 'offset'>;

type ActionProps = { onClose?: () => void };
